import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocalizedPath, buildLocalizedPath } from '../utils/localizedPath.js';
import { SUPPORTED_LANGUAGES, getLanguageByCode } from '../config/languages.js';
import { buildCanonicalUrl, DEFAULT_OG_IMAGE } from '../config/seo.js';

// Shared per-page SEO tags: title, description, robots, canonical +
// hreflang alternates, and OG/Twitter tags. `path` is the unprefixed
// canonical app path (e.g. "/privacy-policy"); omit it (or set
// `noindex`) for pages with no real canonical URL of their own, such
// as a 404 — canonical/hreflang are then skipped entirely.
const Seo = ({
  path,
  title,
  description,
  keywords,
  noindex = false,
  image = DEFAULT_OG_IMAGE,
  imageWidth = 1200,
  imageHeight = 630,
}) => {
  const { lang, localize } = useLocalizedPath();
  const canonicalUrl = path != null && !noindex ? buildCanonicalUrl(localize(path)) : null;
  const currentOgLocale = getLanguageByCode(lang)?.ogLocale || 'en_US';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {canonicalUrl && SUPPORTED_LANGUAGES.map((l) => (
        <link
          key={l.code}
          rel="alternate"
          hrefLang={l.htmlLang}
          href={buildCanonicalUrl(buildLocalizedPath(path, l.code))}
        />
      ))}
      {canonicalUrl && (
        <link rel="alternate" hrefLang="x-default" href={buildCanonicalUrl(buildLocalizedPath(path, 'en'))} />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:site_name" content="Kris Kindler" />
      <meta property="og:locale" content={currentOgLocale} />
      {SUPPORTED_LANGUAGES.filter((l) => l.code !== lang).map((l) => (
        <meta key={l.code} property="og:locale:alternate" content={l.ogLocale} />
      ))}

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
    </Helmet>
  );
};

export default Seo;
